var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
import Base from "../../Base.js";
import Filter from "../../util/CollectionFilter.js";
import FunctionHelper from "../../helper/FunctionHelper.js";
import Collection from "../../util/Collection.js";
import ArrayHelper from "../../helper/ArrayHelper.js";
var StoreFilter_default = (Target) => {
  var _a;
  return _a = class extends (Target || Base) {
    static get defaultConfig() {
      return {
        /**
         * Specify one or more {@link @bryntum/core-thin/lib/util/CollectionFilter} config objects to apply initially.
         *
         * For example:
         *
         * ```javascript
         *  // Configure the store to filter in clients over the age of 30
         *  new Store({
         *      ...,
         *      filters : [{
         *          property : 'age',
         *          value    : 30,
         *          operator : '>'
         *      }],
         *      ...
         *  })
         * ```
         *
         * or:
         *
         * ```javascript
         *  // Configure the store to filter based on a complex operation
         *  new Store({
         *      ...,
         *      filters : [{
         *          filterBy(record) {
         *              // Return true or false for filtering in or out
         *              return shouldClientBeVisible(record);
         *          }
         *      }],
         *      ...
         *  })
         * ```
         *
         * @config {CollectionFilterConfig|CollectionFilterConfig[]}
         * @category Filtering
         */
        filters: null,
        /**
         * Specify true to reapply filters when a record is added to the store.
         * @config {Boolean}
         * @default
         * @category Filtering
         */
        reapplyFilterOnAdd: false,
        /**
         * Specify `true` to reapply filters when a record is updated in the store. You can also provide an array
         * of field names, to only re-filter when certain data fields are updated.
         * @config {Boolean|Object} reapplyFilterOnUpdate
         * @property {String[]} reapplyFilterOnUpdate.fields
         * @default
         * @category Filtering
         */
        reapplyFilterOnUpdate: false
      };
    }
    //endregion
    //region Events
    /**
     * Fired before filtering
     * @event beforeFilter
     * @param {Core.data.Store} source This Store
     * @param {Core.util.Collection} filters Filters to be applied to this Store
     */
    /**
     * Fired after applying filters to the store
     * @event filter
     * @param {Core.data.Store} source This Store
     * @param {Core.util.Collection} filters Filters used by this Store
     * @param {Core.data.Model[]} removed The records which were filtered out by the action.
     * @param {Core.data.Model[]} added The records which were filtered back in by the action.
     * @param {Core.data.Model[]} records Filtered records
     */
    //endregion
    //region Properties
    updateFilterParamName(param) {
      this.remoteFilter = param != null;
    }
    set reapplyFilterOnAdd(enable) {
      this.storage.autoFilter = enable;
    }
    get reapplyFilterOnAdd() {
      return this.storage.autoFilter;
    }
    /**
     * Currently applied filters. A collection of {@link Core.util.CollectionFilter} instances.
     * @type {Core.util.Collection}
     * @readonly
     * @category Sort, group & filter
     */
    set filters(filters) {
      const me = this, collection = me.filters;
      collection.clear();
      me._filtersFunction = null;
      if (filters) {
        if (filters.constructor.name === "Object") {
          for (const f of Object.entries(filters)) {
            if (f[0] === "filterBy" && typeof f[1] === "function") {
              collection.add(new Filter({
                filterBy: f[1]
              }));
            } else {
              collection.add(new Filter(f[1].constructor.name === "Object" ? Object.assign({
                property: f[0]
              }, f[1]) : {
                property: f[0],
                value: f[1]
              }));
            }
          }
        } else if (Array.isArray(filters)) {
          collection.add(...filters.map((filterConfig) => {
            if (filterConfig instanceof Filter) {
              return filterConfig;
            }
            return new Filter(filterConfig);
          }));
        } else if (filters.isCollection) {
          collection.add(...filters.values);
        } else {
          collection.add(new Filter({
            filterBy: filters
          }));
        }
        collection.forEach((item) => item.owner = me);
      }
    }
    get filters() {
      return this._filters || (this._filters = new Collection({ extraKeys: ["property"] }));
    }
    set filtersFunction(filtersFunction) {
      this._filtersFunction = filtersFunction;
    }
    get filtersFunction() {
      const me = this, { filters, isGrouped } = me;
      if (!me._filtersFunction) {
        if (filters.count) {
          const generatedFilterFunction = Filter.generateFiltersFunction(filters);
          me._filtersFunction = (candidate) => {
            if (isGrouped && candidate.isSpecialRow) {
              return candidate.groupChildren.some(generatedFilterFunction);
            }
            if (candidate.isCreating) {
              return true;
            }
            return generatedFilterFunction(candidate);
          };
        } else {
          me._filtersFunction = FunctionHelper.returnTrue;
        }
      }
      return me._filtersFunction;
    }
    /**
     * Check if store is filtered
     * @property {Boolean}
     * @readonly
     * @category Sort, group & filter
     */
    get isFiltered() {
      if (!this._filters && (!this.isConfiguring || !this.initialConfig.filters)) {
        return false;
      }
      return this.filters.values.some((filter) => !filter.disabled);
    }
    isFilteredOut(record) {
      return this.isFiltered && !this.filtersFunction(record);
    }
    //endregion
    traverseFilter(record, deep = true, forceInclusionSet = void 0) {
      const me = this, { filtersFunction } = me, hitsCurrent = !record.isRoot && filtersFunction(record), children = record.unfilteredChildren || record.children;
      if (!children || !children.length) {
        return hitsCurrent;
      }
      if (!record.unfilteredChildren) {
        record.unfilteredChildren = record.children.slice();
      }
      record.children = record.unfilteredChildren.filter(
        (r) => deep ? me.traverseFilter(r, deep) : filtersFunction(r) || (forceInclusionSet == null ? void 0 : forceInclusionSet.has(r))
      );
      if (me.isSorted) {
        record.children.sort(me.sorterFn);
      }
      record.updateChildrenIndices(record.unfilteredChildren, "unfilteredIndex", true);
      record.updateChildrenIndices(record.children, "parentIndex", true);
      return hitsCurrent || Boolean(record.children.length);
    }
    traverseClearFilter(record) {
      const me = this;
      if (record.unfilteredChildren) {
        record.children = record.orderedChildren.slice();
        record.unfilteredChildren = null;
      }
      if (record.children) {
        if (me.isSorted) {
          record.children.sort(me.sorterFn);
        }
        record.children.forEach((r) => me.traverseClearFilter(r));
        record.updateChildrenIndices(record.children, "parentIndex", true);
      }
    }
    get latestFilterField() {
      return this.filters.last ? this.filters.last.property : null;
    }
    /**
     * Adds a single filter to the {@link #config-filters} collection. By default, filters are reevaluated
     * and a Store change event fired.
     *
     * If the `silent` parameter is passed as `true`, multiple filters can be added without causing data changes.
     *
     * When the filters are as required, call {@link #function-filter} with no parameters
     * to apply the filters to the store.
     *
     * @param {CollectionFilterConfig|Function} newFilter A {@link Core.util.CollectionFilter filter} config,
     * or a function to use for filtering.
     * @param {Boolean} [silent] Pass `true` to *not* refilter the store immediately. Such as when
     * adding multiple filters.
     * @returns {Promise|Core.util.CollectionFilter} If {@link @bryntum/core-thin/lib/data/AjaxStore#config-filterParamName} is set on store, this method
     * returns Collection filter inside a `Promise` which is resolved after data is loaded from remote server, otherwise it returns `null`
     * @async
     * @category Sort, group & filter
     */
    addFilter(filter, silent = false) {
      const me = this;
      filter = filter instanceof Filter ? filter : new Filter(filter);
      filter.owner = me;
      me.filters.add(filter);
      if (!silent) {
        if (me.remoteFilter) {
          return me.filter().then(() => filter);
        } else {
          me.filter();
        }
      }
      return filter;
    }
    /**
     * Filters the store by **adding** the specified filter(s) to the existing filters collection applied to this Store.
     * If a filter has an {@link Core.util.CollectionFilter#config-id id} specified,
     * or a {@link Core.util.CollectionFilter#config-property property} specified,
     * it will search for corresponding filter(s) in the existing filters first and replace it with a new filter.
     * **It will not remove other filters applied to the store!**
     *
     * To **add** a new filter:
     *
     * ```javascript
     * // Filter using simple object
     * store.filter({
     *     property : 'age',
     *     operator : '>',
     *     value    : 90
     * });
     *
     * // Filter using function
     * store.filter(r => r.age < 90);
     *
     * // Filter using a named filter as a function
     * store.filter({
     *     id : 'my-filter',
     *     filterBy : record => record.score > 10
     * });
     * ```
     *
     * To **remove** a specific filter, but keep other filters applied
     *
     * ```javascript
     * // Remove by filter `id` or `property`. Filter `id` defaults to the `property-operator` value.
     * store.removeFilter('age');
     * store.removeFilter('my-filter');
     * ```
     *
     * To **replace** all existing filters with a new filter
     *
     * ```javascript
     * // Remove all filters and filter using simple object
     * store.filter({
     *     filters : {
     *         property : 'age',
     *         operator : '<',
     *         value    : 90
     *     },
     *     replace : true
     * });
     *
     * // Remove all filters and filter using function
     * store.filter({
     *     filters : r => r.age > 90,
     *     replace : true
     * });
     *
     * // Remove all filters and filter using a named filter as a function
     * store.filter({
     *     filters : {
     *         id : 'my-filter',
     *         filterBy : record => record.score > 10
     *     },
     *     replace : true
     * });
     * ```
     *
     * Basically filters replacing is an equivalent of having two sequenced calls:
     * {@link #function-clearFilters clearFilters} and {@link #function-filter filter}.
     *
     * Call without arguments to reapply filters.
     *
     * ```javascript
     * // Re-filter the store
     * store.filter();
     * ```
     *
     * @param {Object|CollectionFilterConfig|CollectionFilterConfig[]|Function} newFilters
     *        A {@link Core.util.CollectionFilter filter} config,
     *        or an array of {@link Core.util.CollectionFilter filter} configs,
     *        or a function to use for filtering,
     *        or a special object like: ```{ replace : true, filters : newFilters }```
     * @param {Boolean} [newFilters.replace]
     *        A flag, indicating whether or not the previous filters should be removed.
     * @param {Boolean} [newFilters.silent]
     *        Set as true to not fire events. UI will not be informed about the changes.
     * @param {CollectionFilterConfig|CollectionFilterConfig[]|Function} [newFilters.filters]
     *        If `newFilters` is an object and `replace` property is defined in the `newFilters`,
     *        it means that special object is used and real filter configuration must be nested down to this `filters` property.
     *        It can be:
     *        A {@link Core.util.CollectionFilter filter} config,
     *        or an array of {@link Core.util.CollectionFilter filter} configs,
     *        or a function to use for filtering.
     * @fires filter
     * @fires Core.data.Store#event-change
     * @returns {Promise|null} If {@link @bryntum/core-thin/lib/data/AjaxStore#config-filterParamName} is set on store, this method returns Promise
     * which is resolved after data is loaded from remote server, otherwise it returns null value
     * @async
     * @category Sort, group & filter
     */
    filter(newFilters) {
      const me = this;
      let silent = false, internal;
      if (newFilters) {
        let fieldType = typeof newFilters;
        if (fieldType === "object") {
          if ("silent" in newFilters || "replace" in newFilters || newFilters.filters) {
            silent = newFilters.silent;
            if (newFilters.replace) {
              me.clearFilters(newFilters.silent || newFilters.filters.length !== 0);
            }
            internal = newFilters.internal;
            newFilters = newFilters.filters;
            fieldType = typeof newFilters;
          }
        }
        if (newFilters) {
          const wasFiltered = me.isFiltered;
          me.isConfiguring = true;
          if (Array.isArray(newFilters)) {
            newFilters.forEach((f) => me.addFilter(f, true), me);
          } else if (fieldType === "function") {
            const filter = new Filter(newFilters);
            filter.internal = internal;
            me.addFilter(filter, true);
          } else if (fieldType === "string") {
            me.addFilter({
              property: newFilters,
              value: arguments[1]
            }, true);
          } else {
            me.addFilter(newFilters, true);
          }
          me.isConfiguring = false;
          if (!me.isFiltered && !wasFiltered) {
            return null;
          }
        }
      }
      me.filtersFunction = null;
      const result = me.remoteFilter ? me.performRemoteFilter(silent) : me.performFilter(silent);
      me._idMap = null;
      return result;
    }
    suspendFilterOnUpdate() {
      this.filterBatching++;
    }
    resumeFilterOnUpdate(silent) {
      this.filterBatching--;
      if (this.filterBatching < 0) {
        throw new Error("No matching `startFilterBatching` call");
      }
      if (this.filterBatching === 0 && this.needsFiltering) {
        this.needsFiltering = false;
        this.performFilter(silent);
      }
    }
    /**
     * Perform filtering according to the {@link #property-filters} Collection.
     * This is the internal implementation which is overridden in {@link Core.data.AjaxStore} and
     * must not be overridden.
     * @private
     */
    performFilter(silent) {
      const me = this;
      if (me.filterBatching > 0) {
        me.needsFiltering = true;
        return;
      }
      const { storage, filters, rootNode } = me, oldCount = me.count;
      if (!silent) {
        me.trigger("beforeFilter", { filters });
      }
      let added, removed;
      if (me.tree) {
        const oldDataset = storage.values;
        if (me.isFiltered) {
          me.traverseFilter(rootNode);
        } else {
          me.traverseClearFilter(rootNode);
        }
        const newDataset = me.collectDescendants(rootNode).visible;
        storage.replaceValues({
          values: newDataset,
          silent: true
        });
        const delta = ArrayHelper.delta(newDataset, oldDataset, true);
        added = delta.toAdd;
        removed = delta.toRemove;
      } else {
        storage.ion({
          change({ removed: r, added: a }) {
            removed = r;
            added = a;
          },
          once: true
        });
        if (me.isFiltered) {
          me.isGrouped && me.includeCollapsed();
          storage.addFilter({
            id: "primary-filter",
            filterBy: me.filtersFunction
          });
          me.isGrouped && me.excludeCollapsed();
        } else {
          storage.filters.clear();
        }
      }
      me.afterPerformFilter(silent || me.isRemoteDataLoading ? null : {
        action: "filter",
        filters,
        oldCount,
        added,
        removed,
        records: me.storage.values
      });
    }
    async performRemoteFilter(silent) {
      var _a2;
      const me = this, {
        filters,
        count: oldCount
      } = me;
      if (await ((_a2 = me.beforePerformFilter) == null ? void 0 : _a2.call(me, { filters, oldCount })) === false) {
        return;
      }
      me.currentPage = 1;
      if (await me.performDataRequest(silent) === false) {
        return;
      }
      const event = silent ? null : {
        action: "filter",
        filters,
        oldCount,
        records: me.storage.values
      };
      me.afterPerformFilter(event);
      me.trigger("refresh", event);
    }
    buildRemoteParams(params = {}) {
      var _a2, _b;
      (_a2 = super.buildRemoteParams) == null ? void 0 : _a2.call(this, params);
      if (this.isFiltered) {
        params[(_b = this.filterParamName) != null ? _b : "filters"] = this.encodeFilterParams(this.filters);
      }
      return params;
    }
    afterPerformFilter(event) {
      this.resetRelationCache();
      if (event) {
        this.triggerFilterEvent(event);
      }
    }
    // For AjaxStore
    encodeFilterParams(filters) {
      return this.getFilterParams(filters);
    }
    getFilterParams(filters) {
      return filters.values.filter((f) => !f.disabled).map(({ property, operator, value, caseSensitive }) => ({
        field: property,
        operator,
        value,
        caseSensitive
      }));
    }
    // Used from filter() and StoreCRUD when reapplying filters
    triggerFilterEvent(event) {
      this.trigger("filter", event);
      if (!this.remoteFilter) {
        this.trigger("refresh", event);
        this.trigger("change", event);
      }
    }
    /**
     * *Adds* a function used to filter the store. Alias for calling `filter(fn)`. Return `true` from the function to
     * include record in filtered set
     *
     * ```javascript
     * store.filterBy(record => record.age > 25 && record.name.startsWith('A'));
     * ```
     *
     * @param {Function} fn Function used to test records
     * @returns {Promise|null} If {@link @bryntum/core-thin/lib/data/AjaxStore#config-filterParamName} is set on store, this method returns `Promise`
     * which is resolved after data is loaded from remote server, otherwise it returns `null`
     * @async
     * @category Sort, group & filter
     */
    filterBy(fn) {
      return this.filter(fn);
    }
    /**
     * Removes the passed filter, or the filter by the passed ID from the {@link #config-filters} collection.
     * By default, filters are reevaluated and a Store change event fired.
     *
     * If the `silent` parameter is passed as `true`, multiple filters can be removed without causing data changes.
     *
     * When the filters are as required, call {@link #function-filter} with no parameters
     * to apply the filters to the store.
     *
     * ```javascript
     * // Only view top priority events
     * myEventStore.filter({
     *     id       : 'priorityFilter',
     *     property : 'priority',
     *     value    : 1,
     *     operator : '='
     * });
     *
     * // That individual filter can be removed like this
     * myEventStore.removeFilter('priorityFilter');
     *
     * // Add named filter as a function
     * store.filter({
     *     id : 'my filter',
     *     filterBy : record => record.score > 10
     * });
     *
     * // Remove named filter function
     * store.removeFilter('my filter');
     * ```
     *
     * @param {String|Core.util.CollectionFilter} idOrInstance Filter to remove, or ID of the filter to remove. By default,
     * filters are reevaluated and a change event fired.
     * @param {Boolean} [silent] Pass `true` to *not* refilter the store immediately. Such as when
     * removing multiple filters.
     * @returns {Promise|Core.util.CollectionFilter} If {@link @bryntum/core-thin/lib/data/AjaxStore#config-filterParamName} is set on store, this method
     * returns Collection filter inside a `Promise` which is resolved after data is loaded from remote server, otherwise it returns `null`
     * @async
     * @category Sort, group & filter
     */
    removeFilter(idOrInstance, silent = false) {
      var _a2, _b;
      const me = this, filter = idOrInstance instanceof Filter ? idOrInstance : (_a2 = me.filters.get(idOrInstance)) != null ? _a2 : me.filters.getBy("property", idOrInstance);
      if (filter) {
        (_b = me._filters) == null ? void 0 : _b.remove(filter);
        me._filtersFunction = null;
        if (!silent) {
          if (me.remoteFilter) {
            return me.filter().then(() => filter);
          } else {
            me.filter();
          }
        }
        return filter;
      }
    }
    /**
     * Removes all filters from the store.
     * @param {Boolean} [silent] Pass `true` to *not* refilter the store immediately.
     * which is resolved after data is loaded from remote server, otherwise it returns `null`
     * @returns {Promise|null} If {@link @bryntum/core-thin/lib/data/AjaxStore#config-filterParamName} is set on store, this method returns `Promise`
     * @async
     * @category Sort, group & filter
     */
    clearFilters(silent = false) {
      this.filters.remove(this.filters.values.filter((f) => !f.internal));
      if (!silent) {
        return this.filter();
      }
    }
    convertFilterToString(field) {
      const filter = this.filters.getBy("property", field);
      return filter && !filter.filterBy ? String(filter) : "";
    }
    doDestroy() {
      var _a2;
      (_a2 = this._filters) == null ? void 0 : _a2.destroy();
      super.doDestroy();
    }
  }, __publicField(_a, "$name", "StoreFilter"), //region Config
  __publicField(_a, "configurable", {
    /**
     * Set this to `true` to activate remote filtering in this Store. This makes it possible to use the built-in
     * filtering features of the Store and corresponding UI functionality, without using local data.
     *
     * For a non-{@link Core.data.AjaxStore}, data will be requested by the Store by calling a by the app
     * implemented {@link Core.data.Store#function-requestData} function. Data can also be provided by listening to
     * the {@link Core.data.Store#event-requestData} event, and updating the {@link Core.data.Store#config-data}
     * property with new data.
     *
     * For {@link Core.data.AjaxStore}, data will be loaded via the configured
     * {@link Core.data.AjaxStore#config-readUrl}.
     *
     * @config {Boolean}
     * @category Filtering
     */
    remoteFilter: false,
    /**
     * The name of the parameter to use to pass any filters when loading data remotely.
     *
     * **Note:** When this is set, filters must be defined using a field name, an operator and a value
     * to compare, **not** a comparison function.
     * @config {String}
     * @category Remote
     */
    filterParamName: null
  }), __publicField(_a, "properties", {
    filterBatching: 0,
    needsFiltering: false
  }), _a;
};
export {
  StoreFilter_default as default
};
